import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { SettingsModal } from './SettingsModal';
import { PermissionBadge } from './PermissionBadge';
import * as api from '../services/settingsApi';
import {
    Shield, Key, Plus, Search, ShieldOff, ShieldCheck, AlertCircle, Loader2,
} from 'lucide-react';

interface UserPermissionOverridesPanelProps {
    open: boolean;
    onClose: () => void;
    userId: string;
    userName: string;
    userEmail: string;
}

export function UserPermissionOverridesPanel({
    open,
    onClose,
    userId,
    userName,
    userEmail,
}: UserPermissionOverridesPanelProps) {
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [searchTerm, setSearchTerm] = useState('');
    const [grantId, setGrantId] = useState('');
    const [revokeId, setRevokeId] = useState('');
    const [grantReason, setGrantReason] = useState('');
    const [revokeReason, setRevokeReason] = useState('');

    // Fetch user's roles
    const { data: userRoles, isLoading: rolesLoading } = useQuery({
        queryKey: ['userRoles', tenantId, userId],
        queryFn: () => api.getUserRoles(tenantId!, userId),
        enabled: !!tenantId && open,
    });

    // Fetch all permissions catalog
    const { data: allPermissions, isLoading: permsLoading } = useQuery({
        queryKey: ['permissions'],
        queryFn: () => api.getPermissions(),
        enabled: open,
    });

    // Fetch user's overrides
    const { data: overrides, isLoading: overridesLoading } = useQuery({
        queryKey: ['userPermissionOverrides', userId],
        queryFn: () => api.getUserPermissionOverrides(userId),
        enabled: !!userId && open,
    });

    // Grant permission mutation
    const grantMutation = useMutation({
        mutationFn: () => api.grantUserPermission(userId, grantId, grantReason || undefined),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userPermissionOverrides', userId] });
            addToast({ type: 'success', title: 'Permission granted' });
            setGrantId('');
            setGrantReason('');
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed',
                message: err?.response?.data?.error?.message || 'An error occurred',
            });
        },
    });

    // Revoke permission mutation
    const revokeMutation = useMutation({
        mutationFn: () => api.revokeUserPermission(userId, revokeId, revokeReason || undefined),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userPermissionOverrides', userId] });
            addToast({ type: 'success', title: 'Permission revoked' });
            setRevokeId('');
            setRevokeReason('');
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed',
                message: err?.response?.data?.error?.message || 'An error occurred',
            });
        },
    });

    // Remove override mutation
    const removeOverrideMutation = useMutation({
        mutationFn: (permissionId: string) => api.removeUserPermissionOverride(userId, permissionId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userPermissionOverrides', userId] });
            addToast({ type: 'success', title: 'Override removed' });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed',
                message: err?.response?.data?.error?.message || 'An error occurred',
            });
        },
    });

    const isLoading = rolesLoading || permsLoading || overridesLoading;

    // Build override lookup maps
    const grantedOverrideIds = new Set(
        (overrides || []).filter((o: any) => o.granted).map((o: any) => `${o.module}:${o.action}`)
    );
    const deniedOverrideIds = new Set(
        (overrides || []).filter((o: any) => !o.granted).map((o: any) => `${o.module}:${o.action}`)
    );
    const overrideByPermId = new Map(
        (overrides || []).map((o: any) => [o.id, o])
    );

    // Filter permissions by search
    const filteredPermissions = (allPermissions || []).filter((p: any) => {
        const term = searchTerm.toLowerCase();
        return (
            p.module.toLowerCase().includes(term) ||
            p.action.toLowerCase().includes(term) ||
            (p.description || '').toLowerCase().includes(term)
        );
    });

    // Group permissions by module
    const groupedPermissions: Record<string, any[]> = {};
    for (const perm of filteredPermissions) {
        if (!groupedPermissions[perm.module]) {
            groupedPermissions[perm.module] = [];
        }
        groupedPermissions[perm.module].push(perm);
    }

    // Available for grant: permissions NOT already granted
    const availableForGrant = filteredPermissions.filter(
        (p: any) => !grantedOverrideIds.has(`${p.module}:${p.action}`)
    );

    // Available for revoke: permissions NOT already denied
    const availableForRevoke = filteredPermissions.filter(
        (p: any) => !deniedOverrideIds.has(`${p.module}:${p.action}`)
    );

    return (
        <SettingsModal
            open={open}
            onClose={onClose}
            title="Permission Overrides"
            subtitle={`Manage individual permissions for ${userName}`}
            icon={Key}
            size="xl"
            footer={
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            }
        >
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 size={32} className="animate-spin text-slate-300" />
                </div>
            ) : (
                <div className="space-y-6">
                    {/* User Info & Roles */}
                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold text-slate-700">{userName}</p>
                                <p className="text-xs text-slate-500">{userEmail}</p>
                            </div>
                            <div className="flex flex-wrap gap-1.5 max-w-[300px] justify-end">
                                {(userRoles || []).map((role: any) => (
                                    <span
                                        key={role.id}
                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700"
                                    >
                                        <Shield size={10} />
                                        {role.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Active Overrides Summary */}
                    {(overrides || []).length > 0 && (
                        <div>
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                Active Overrides ({(overrides || []).length})
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {(overrides || []).map((override: any) => (
                                    <PermissionBadge
                                        key={override.id}
                                        label={`${override.module}:${override.action}`}
                                        description={override.reason || override.description || undefined}
                                        type={override.granted ? 'granted' : 'denied'}
                                        onRemove={() => removeOverrideMutation.mutate(override.id)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quick Actions: Grant / Revoke */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Grant Permission */}
                        <div className="p-4 bg-emerald-50/50 rounded-xl border border-emerald-200">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="p-1.5 rounded-lg bg-emerald-100">
                                    <ShieldCheck size={16} className="text-emerald-600" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-emerald-800">Grant Permission</h4>
                                    <p className="text-xs text-emerald-600">
                                        Explicitly allow a permission
                                    </p>
                                </div>
                            </div>
                            <select
                                value={grantId}
                                onChange={(e) => setGrantId(e.target.value)}
                                className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm mb-2 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 outline-none bg-white"
                            >
                                <option value="">Select permission to grant...</option>
                                {availableForGrant.map((p: any) => (
                                    <option key={p.id} value={p.id}>
                                        {p.module}:{p.action} — {p.description || ''}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Reason (optional)"
                                value={grantReason}
                                onChange={(e) => setGrantReason(e.target.value)}
                                className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm mb-2 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 outline-none bg-white"
                            />
                            <Button
                                size="sm"
                                fullWidth
                                onClick={() => grantMutation.mutate()}
                                isLoading={grantMutation.isPending}
                                disabled={!grantId}
                                leftIcon={<Plus size={14} />}
                            >
                                Grant Permission
                            </Button>
                        </div>

                        {/* Revoke Permission */}
                        <div className="p-4 bg-rose-50/50 rounded-xl border border-rose-200">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="p-1.5 rounded-lg bg-rose-100">
                                    <ShieldOff size={16} className="text-rose-600" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-rose-800">Revoke Permission</h4>
                                    <p className="text-xs text-rose-600">
                                        Explicitly deny a permission
                                    </p>
                                </div>
                            </div>
                            <select
                                value={revokeId}
                                onChange={(e) => setRevokeId(e.target.value)}
                                className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm mb-2 focus:border-rose-500 focus:ring-1 focus:ring-rose-500/20 outline-none bg-white"
                            >
                                <option value="">Select permission to revoke...</option>
                                {availableForRevoke.map((p: any) => (
                                    <option key={p.id} value={p.id}>
                                        {p.module}:{p.action} — {p.description || ''}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="text"
                                placeholder="Reason (optional)"
                                value={revokeReason}
                                onChange={(e) => setRevokeReason(e.target.value)}
                                className="w-full px-3 py-2 border border-rose-200 rounded-lg text-sm mb-2 focus:border-rose-500 focus:ring-1 focus:ring-rose-500/20 outline-none bg-white"
                            />
                            <Button
                                size="sm"
                                fullWidth
                                onClick={() => revokeMutation.mutate()}
                                isLoading={revokeMutation.isPending}
                                disabled={!revokeId}
                                leftIcon={<AlertCircle size={14} />}
                                className="bg-rose-500 hover:bg-rose-600 text-white"
                            >
                                Revoke Permission
                            </Button>
                        </div>
                    </div>

                    {/* All Permissions Catalog */}
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                All Permissions
                            </h4>
                            <div className="relative w-64">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search permissions..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 outline-none"
                                />
                            </div>
                        </div>

                        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                            {Object.entries(groupedPermissions).map(([module, perms]) => (
                                <div
                                    key={module}
                                    className="border border-slate-200 rounded-xl overflow-hidden"
                                >
                                    <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                                        <h5 className="text-sm font-semibold text-slate-700 capitalize">
                                            {module}
                                        </h5>
                                    </div>
                                    <div className="p-2 flex flex-wrap gap-1.5">
                                        {perms.map((perm: any) => {
                                            const key = `${perm.module}:${perm.action}`;
                                            const isGranted = grantedOverrideIds.has(key);
                                            const isDenied = deniedOverrideIds.has(key);

                                            return (
                                                <PermissionBadge
                                                    key={perm.id}
                                                    label={perm.action}
                                                    description={perm.description || undefined}
                                                    type={
                                                        isGranted
                                                            ? 'granted'
                                                            : isDenied
                                                                ? 'denied'
                                                                : 'role'
                                                    }
                                                    size="sm"
                                                    onRemove={
                                                        isGranted || isDenied
                                                            ? () => removeOverrideMutation.mutate(perm.id)
                                                            : undefined
                                                    }
                                                />
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {Object.keys(groupedPermissions).length === 0 && (
                                <p className="text-sm text-slate-400 text-center py-8">
                                    No permissions match your search
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </SettingsModal>
    );
}